import {useEffect, useId, useRef, type KeyboardEvent} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {Moon, Phone} from 'lucide-react';
import {useAppDispatch, type RootState} from '../../../store/store';
import {selectTenant, selectUserId} from '../../../store/features/Auth/authSlice';
import {dismissDndCall} from '../store/callSurfaceSlice';
import {startInternalCall, type InternalCallee} from './matrixActions';
import {matrixSurfaceMessages as m} from './matrixMessages';

const asCallee = (value: unknown): InternalCallee | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const record = value as {userId?: unknown; firstName?: unknown; lastName?: unknown};
    return typeof record.userId === 'string' || typeof record.userId === 'number'
        ? {
              userId: record.userId,
              firstName: typeof record.firstName === 'string' ? record.firstName : undefined,
              lastName: typeof record.lastName === 'string' ? record.lastName : undefined,
          }
        : undefined;
};

export const DndCallDialog = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const callee = asCallee(useSelector((state: RootState) => state.dialer.callFrom));
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const request = useSelector((state: RootState) => state.callSurface.dndPrompt);
    const currentUserId = useSelector(selectUserId);
    const tenant = useSelector(selectTenant);
    const titleId = useId();
    const confirm = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        confirm.current?.focus();
    }, []);

    if (!callee) {
        return null;
    }

    const close = () => dispatch(dismissDndCall());
    const callAnyway = () => {
        close();
        void startInternalCall(callee, {
            video: request?.video,
            roomId: request?.roomId,
            callState,
            currentUserId: String(currentUserId ?? ''),
            tenant: String(tenant ?? ''),
            dispatch,
        });
    };
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            close();
        }
    };

    return createPortal(
        <div className="phq-call-light fixed inset-0 z-[80] flex items-center justify-center bg-[var(--phq-call-scrim)] p-4" onKeyDown={onKeyDown}>
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid="dnd-call-dialog"
                className="phq-call-enter flex w-[340px] max-w-full flex-col items-center gap-2 rounded-[20px] bg-[var(--phq-call-surface)] p-6 text-center phq-call-shadow-overlay"
            >
                <span className="mb-2 inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-[var(--phq-call-brand-tint)] text-[var(--phq-call-brand-ink)]">
                    <Moon size={32} aria-hidden="true"/>
                </span>
                <h2 id={titleId} className="text-lg font-semibold">{intl.formatMessage(m.dndTitle, {name: callee.firstName ?? ''})}</h2>
                <p className="text-sm leading-[1.5] text-[var(--phq-call-ink-secondary)]">{intl.formatMessage(m.dndBody)}</p>
                <div className="flex gap-2 pt-3">
                    <button type="button" onClick={close} className="phq-call-press h-9 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-4 text-sm font-semibold">
                        {intl.formatMessage(m.dndCancel)}
                    </button>
                    <button
                        ref={confirm}
                        type="button"
                        data-testid="dnd-call-anyway"
                        onClick={callAnyway}
                        className="phq-call-press inline-flex h-9 items-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-4 text-sm font-semibold text-white"
                    >
                        <Phone size={16} aria-hidden="true"/>
                        {intl.formatMessage(m.dndCallAnyway)}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
